import React, { useRef, useEffect } from "react";
import {
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Volume2,
  VolumeX,
  ListMusic,
  X,
  Heart,
  MonitorSpeaker,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { usePlayer } from "../contexts/PlayerContext";
import WaveformVisualizer from "./WaveformVisualizer";

const formatTime = (seconds: number): string => {
  if (isNaN(seconds) || seconds === 0) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

const MusicPlayer: React.FC = () => {
  const {
    currentTrack,
    isPlaying,
    progress,
    duration,
    volume,
    isMuted,
    isLoading,
    queue,
    playTrack,
    pauseTrack,
    resumeTrack,
    nextTrack,
    previousTrack,
    seekTo,
    setVolume,
    toggleMute,
  } = usePlayer();

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const [showQueue, setShowQueue] = React.useState(false);
  const [showExternalPlayer, setShowExternalPlayer] = React.useState(false);

  // Auto-show external player when a Mixcloud track starts
  useEffect(() => {
    if (currentTrack?.source === "MIXCLOUD") {
      setShowExternalPlayer(true);
    } else {
      setShowExternalPlayer(false);
    }
  }, [currentTrack?.id, currentTrack?.source]);

  useEffect(() => {
    if (!audioRef.current && typeof window !== "undefined") {
      audioRef.current = new Audio();
    }
  }, []);

  if (!currentTrack) return null;

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!progressRef.current || !duration) return;
    const rect = progressRef.current.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    seekTo(percent * duration);
  };

  const progressPercent = duration ? (progress / duration) * 100 : 0;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        className="fixed bottom-0 left-0 right-0 z-50"
      >
        {/* Main Player Bar */}
        <div className="bg-white/10 backdrop-blur-xl border-t border-white/10 shadow-2xl">
          <div className="max-w-screen-2xl mx-auto">
            {/* Progress Bar */}
            <div
              ref={progressRef}
              className="h-1 bg-white/10 cursor-pointer group"
              onClick={handleProgressClick}
            >
              <motion.div
                className="h-full bg-gradient-to-r from-gold to-amber-400 relative"
                style={{ width: `${progressPercent}%` }}
              >
                <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-gold rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-glow-gold" />
              </motion.div>
            </div>

            <div className="flex items-center justify-between px-4 py-3">
              {/* Track Info */}
              <div className="flex items-center space-x-4 flex-1 min-w-0">
                <div className="relative w-14 h-14 rounded-lg overflow-hidden flex-shrink-0 bg-white/5">
                  <img
                    src={
                      currentTrack.coverImage ||
                      "/images/PRIMARY-LOGO-DESATURATED.png"
                    }
                    alt={currentTrack.title}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src =
                        "/images/PRIMARY-LOGO-DESATURATED.png";
                    }}
                  />
                  {isLoading && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <div className="w-4 h-4 border-2 border-gold border-t-transparent rounded-full animate-spin" />
                    </div>
                  )}
                </div>
                <div className="min-w-0">
                  <h4 className="text-white font-medium truncate">
                    {currentTrack.title}
                  </h4>
                  <p className="text-white-subtle text-sm truncate">
                    {currentTrack.artist}
                  </p>
                </div>
                <button className="p-2 text-white-subtle hover:text-gold transition-colors">
                  <Heart size={18} />
                </button>
              </div>

              {/* Center Controls */}
              <div className="flex flex-col items-center flex-1 max-w-xl">
                <div className="flex items-center space-x-4">
                  <button
                    onClick={previousTrack}
                    className="p-2 text-white-subtle hover:text-white transition-colors"
                    aria-label="Previous track"
                  >
                    <SkipBack size={20} />
                  </button>

                  <button
                    onClick={() => (isPlaying ? pauseTrack() : resumeTrack())}
                    className="p-3 bg-gold text-black rounded-full hover:bg-gold-600 transition-colors shadow-glow-gold hover:shadow-glow-gold"
                    aria-label={isPlaying ? "Pause" : "Play"}
                  >
                    {isPlaying ? (
                      <Pause size={22} fill="currentColor" />
                    ) : (
                      <Play size={22} fill="currentColor" className="ml-0.5" />
                    )}
                  </button>

                  <button
                    onClick={nextTrack}
                    className="p-2 text-white-subtle hover:text-white transition-colors"
                    aria-label="Next track"
                  >
                    <SkipForward size={20} />
                  </button>
                </div>

                {/* Time Display */}
                <div className="flex items-center space-x-2 mt-2 text-xs text-white-subtle">
                  <span>{formatTime(progress)}</span>
                  <span>/</span>
                  <span>{formatTime(duration)}</span>
                </div>
              </div>

              {/* Right Side - Visualizer & Volume */}
              <div className="flex items-center justify-end flex-1 space-x-4">
                {/* Waveform Visualizer */}
                <div className="hidden md:block">
                  <WaveformVisualizer
                    audioElement={audioRef.current}
                    isPlaying={isPlaying}
                    barCount={24}
                    barWidth={3}
                    barGap={2}
                    maxBarHeight={36}
                    minBarHeight={3}
                  />
                </div>

                {/* Volume */}
                <div className="flex items-center space-x-2 group">
                  <button
                    onClick={toggleMute}
                    className="p-2 text-white-subtle hover:text-white transition-colors"
                    aria-label={isMuted ? "Unmute" : "Mute"}
                  >
                    {isMuted || volume === 0 ? (
                      <VolumeX size={18} />
                    ) : (
                      <Volume2 size={18} />
                    )}
                  </button>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={isMuted ? 0 : volume}
                    onChange={(e) => setVolume(parseFloat(e.target.value))}
                    className="w-20 h-1 bg-white/20 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:bg-gold [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer"
                    aria-label="Volume"
                  />
                </div>

                {/* External Player Toggle */}
                {currentTrack.source && currentTrack.source !== "LOCAL" && (
                  <button
                    onClick={() => setShowExternalPlayer(!showExternalPlayer)}
                    className={`p-2 transition-colors ${showExternalPlayer ? "text-gold" : "text-white-subtle hover:text-white"}`}
                    aria-label="Toggle external player"
                    title="View Original Player"
                  >
                    <MonitorSpeaker size={18} />
                  </button>
                )}

                {/* Queue Toggle */}
                <button
                  onClick={() => setShowQueue(!showQueue)}
                  className={`p-2 transition-colors ${showQueue ? "text-gold" : "text-white-subtle hover:text-white"}`}
                  aria-label="Toggle queue"
                >
                  <ListMusic size={18} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* External Player Panel */}
        <AnimatePresence>
          {showExternalPlayer &&
            currentTrack.source &&
            currentTrack.source !== "LOCAL" && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="absolute bottom-full left-0 right-0 bg-black/95 backdrop-blur-2xl border-t border-white/10 overflow-hidden shadow-2xl"
              >
                <div className="max-w-screen-2xl mx-auto p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <MonitorSpeaker size={16} className="text-gold" />
                      <span className="text-xs font-semibold text-white uppercase tracking-wider">
                        {currentTrack.source} Player
                      </span>
                    </div>
                    <button
                      onClick={() => setShowExternalPlayer(false)}
                      className="p-1 text-white-subtle hover:text-white"
                    >
                      <X size={16} />
                    </button>
                  </div>
                  <div className="w-full bg-white/5 rounded-lg overflow-hidden min-h-[120px] flex items-center justify-center border border-white/5">
                    <div className="w-full h-full min-h-[120px]">
                      <div
                        id="external-player-container"
                        className="w-full h-full"
                      ></div>
                    </div>
                  </div>
                  <div className="mt-2 text-[10px] text-white/40 text-center uppercase tracking-widest">
                    Official {currentTrack.source} Widget Required for Playback
                  </div>
                </div>
              </motion.div>
            )}
        </AnimatePresence>

        {/* Queue Panel */}
        <AnimatePresence>
          {showQueue && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className="absolute bottom-full right-4 mb-2 w-80 max-h-96 bg-white/10 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl overflow-hidden"
            >
              <div className="flex items-center justify-between p-3 border-b border-white/10">
                <h3 className="text-white font-medium">Queue</h3>
                <button
                  onClick={() => setShowQueue(false)}
                  className="p-1 text-white-subtle hover:text-white"
                >
                  <X size={16} />
                </button>
              </div>
              <div className="overflow-y-auto max-h-72">
                {queue.length === 0 ? (
                  <p className="p-4 text-white-subtle text-sm text-center">
                    No tracks in queue
                  </p>
                ) : (
                  queue.map((track, index) => (
                    <div
                      key={track.id || index}
                      className="flex items-center p-3 hover:bg-white/5 cursor-pointer transition-colors"
                      onClick={() => playTrack(track)}
                    >
                      <div className="w-10 h-10 rounded overflow-hidden flex-shrink-0 bg-white/5">
                        <img
                          src={
                            track.coverImage ||
                            "/images/PRIMARY-LOGO-DESATURATED.png"
                          }
                          alt={track.title}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src =
                              "/images/PRIMARY-LOGO-DESATURATED.png";
                          }}
                        />
                      </div>
                      <div className="ml-3 min-w-0">
                        <p className="text-white text-sm truncate">
                          {track.title}
                        </p>
                        <p className="text-white-subtle text-xs truncate">
                          {track.artist}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>
  );
};

export default MusicPlayer;
